import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { api, getApiErrorMessage } from '../lib/api'
import MenuGuideButton from '../components/MenuGuideButton'
import { useToast } from '../ui/ToastProvider'

const EMPTY_MAPPING = {
    store: 'apple_iap',
    productId: '',
    planCode: '',
    periodMonths: 1,
    currency: 'KRW',
    storePrice: '',
    displayName: '',
    memo: '',
    active: true,
}

const STORE_LABELS = {
    apple_iap: 'App Store',
    google_play: 'Google Play',
    web_pg: 'Web PG',
    toss: 'Toss Payments',
    toss_pg: 'Toss Payments',
    portone: 'PortOne',
    portone_pg: 'PortOne',
    inicis: 'KG Inicis',
    nicepay: 'Nicepay',
    mock: 'Mock PG',
}

const VISIBLE_PLAN_ORDER = ['TRIAL', 'BASIC', 'ENTERPRISE']
const VISIBLE_PLAN_ORDER_INDEX = new Map(VISIBLE_PLAN_ORDER.map((code, index) => [code, index]))
const POINT_OVERAGE_POLICIES = new Set([
    'ALLOW_WITH_POINT', 'POINT', 'POINTS', 'POINT_FALLBACK', 'POINT_OVERAGE',
    'PAY_AS_YOU_GO_POINTS', 'ALWAYS_POINTS', 'POINT_ONLY',
])
const CLAIM_FAX_FEATURE_CODE = 'CLAIM_FAX'
const CLAIM_FAX_PAGE_FEATURE_CODE = 'CLAIM_FAX_PAGE'
const POINT_PRICING_PER_UNIT = 'PER_UNIT'
const POINT_PRICING_BASE_PLUS_EXTRA = 'BASE_PLUS_EXTRA'

function usesPointOverage(policy) {
    return POINT_OVERAGE_POLICIES.has(String(policy || '').trim().toUpperCase())
}

function canConfigurePointOverage(feature) {
    return Boolean(feature?.enabled)
        && String(feature?.quotaMode || '').toUpperCase() === 'LIMITED'
        && feature?.featureCode !== CLAIM_FAX_PAGE_FEATURE_CODE
}

function resetPointPricing(row, changes = {}) {
    return {
        ...row,
        ...changes,
        overagePolicy: 'BLOCK',
        pointCost: 0,
        pointPricingMode: POINT_PRICING_PER_UNIT,
        pointIncludedUnits: 0,
        pointExtraUnitCost: 0,
    }
}

function quotaUnitLabel(unit) {
    switch (String(unit || '').toUpperCase()) {
        case 'COUNT': return '건'
        case 'PAGE': return '장'
        case 'RUN': return '회'
        case 'ACCESS': return '이용'
        case 'MB': return 'MB'
        case 'GB': return 'GB'
        default: return unit || '-'
    }
}

function visibleBillingPlans(plans) {
    return [...plans]
        .filter((plan) => VISIBLE_PLAN_ORDER_INDEX.has(String(plan.planCode || '').toUpperCase()))
        .sort((a, b) => {
            const ai = VISIBLE_PLAN_ORDER_INDEX.get(String(a.planCode || '').toUpperCase()) ?? 999
            const bi = VISIBLE_PLAN_ORDER_INDEX.get(String(b.planCode || '').toUpperCase()) ?? 999
            return ai - bi
        })
}


export default function BillingConfigPage() {
    const { show } = useToast()
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [plans, setPlans] = useState([])
    const [featurePolicies, setFeaturePolicies] = useState([])
    const [storeProducts, setStoreProducts] = useState([])
    const [newMapping, setNewMapping] = useState(EMPTY_MAPPING)

    const loadConfigs = useCallback(async () => {
        setLoading(true)
        try {
            const [plansRes, featuresRes, storeProductsRes] = await Promise.all([
                api.get('/billing/admin/config/plans'),
                api.get('/billing/admin/config/plan-features'),
                api.get('/billing/admin/config/channel-products'),
            ])
            const loadedPlans = Array.isArray(plansRes.data?.data) ? plansRes.data.data : []
            const nextPlans = visibleBillingPlans(loadedPlans)
            setPlans(nextPlans)
            setFeaturePolicies(Array.isArray(featuresRes.data?.data) ? featuresRes.data.data : [])
            setStoreProducts(Array.isArray(storeProductsRes.data?.data) ? storeProductsRes.data.data : [])
            setNewMapping((prev) => ({
                ...prev,
                planCode: nextPlans.some((plan) => plan.planCode === prev.planCode) ? prev.planCode : nextPlans[0]?.planCode || '',
            }))
        } catch (e) {
            show('과금 설정을 불러오지 못했습니다.', 'error')
        } finally {
            setLoading(false)
        }
    }, [show])

    useEffect(() => {
        loadConfigs()
    }, [loadConfigs])

    const featureMap = useMemo(() => {
        return featurePolicies.reduce((acc, row) => {
            acc[row.planCode] = acc[row.planCode] || []
            acc[row.planCode].push(row)
            return acc
        }, {})
    }, [featurePolicies])

    const storeProductMap = useMemo(() => {
        return storeProducts.reduce((acc, row) => {
            acc[row.planCode] = acc[row.planCode] || []
            acc[row.planCode].push(row)
            return acc
        }, {})
    }, [storeProducts])

    const updatePlanField = (planId, field, value) => {
        setPlans((prev) => prev.map((plan) => (
            plan.id === planId ? { ...plan, [field]: value } : plan
        )))
    }

    const updateFeaturePolicy = (planCode, featureCode, updater) => {
        setFeaturePolicies((prev) => prev.map((row) => (
            row.planCode === planCode && row.featureCode === featureCode
                ? updater(row)
                : row
        )))
    }

    const updateFeatureField = (planCode, featureCode, field, value) => {
        updateFeaturePolicy(planCode, featureCode, (row) => ({ ...row, [field]: value }))
    }

    const updateFeatureEnabled = (planCode, featureCode, enabled) => {
        updateFeaturePolicy(planCode, featureCode, (row) => enabled
            ? { ...row, enabled }
            : resetPointPricing(row, { enabled }))
    }

    const updateFeatureQuotaMode = (planCode, featureCode, quotaMode) => {
        updateFeaturePolicy(planCode, featureCode, (row) => quotaMode === 'LIMITED'
            ? { ...row, quotaMode }
            : resetPointPricing(row, { quotaMode }))
    }

    const updateFeatureOveragePolicy = (planCode, featureCode, overagePolicy) => {
        updateFeaturePolicy(planCode, featureCode, (row) => {
            if (overagePolicy !== 'ALLOW_WITH_POINT' || featureCode === CLAIM_FAX_PAGE_FEATURE_CODE) {
                return resetPointPricing(row)
            }
            const claimPricing = featureCode === CLAIM_FAX_FEATURE_CODE
            return {
                ...row,
                overagePolicy,
                pointPricingMode: claimPricing ? POINT_PRICING_BASE_PLUS_EXTRA : POINT_PRICING_PER_UNIT,
                pointIncludedUnits: claimPricing ? Number(row.pointIncludedUnits) || 0 : 0,
                pointExtraUnitCost: claimPricing ? Number(row.pointExtraUnitCost) || 0 : 0,
            }
        })
    }

    const updateStoreProductField = (id, field, value) => {
        setStoreProducts((prev) => prev.map((row) => (
            row.id === id ? { ...row, [field]: value } : row
        )))
    }

    const savePlan = async (plan) => {
        setSaving(true)
        try {
            await api.put(`/billing/admin/config/plans/${plan.id}`, plan)
            show(`${plan.planCode} 플랜이 저장되었습니다.`, 'success')
            loadConfigs()
        } catch (err) {
            show('플랜 저장에 실패했습니다.', 'error')
        } finally {
            setSaving(false)
        }
    }

    const saveFeatures = async (planVersionId, planCode) => {
        const rows = featurePolicies.filter((row) => row.planCode === planCode)
        const invalidPointPolicy = rows.find((row) => (
            row.featureCode !== CLAIM_FAX_PAGE_FEATURE_CODE
            && usesPointOverage(row.overagePolicy)
            && !canConfigurePointOverage(row)
        ))
        if (invalidPointPolicy) {
            show(`${invalidPointPolicy.featureName || '기능'}: 포인트 추가 사용은 제공 중인 제한형 쿼터에서만 설정할 수 있습니다.`, 'error')
            return
        }
        const invalidUnitPrice = rows.find((row) => (
            row.featureCode !== CLAIM_FAX_FEATURE_CODE
            && usesPointOverage(row.overagePolicy)
            && (!Number.isInteger(Number(row.pointCost)) || Number(row.pointCost) < 1)
        ))
        if (invalidUnitPrice) {
            show(`${invalidUnitPrice.featureName || '기능'}: 1P 이상의 건당 차감 포인트가 필요합니다.`, 'error')
            return
        }
        const claimPolicy = rows.find((row) => row.featureCode === CLAIM_FAX_FEATURE_CODE)
        if (usesPointOverage(claimPolicy?.overagePolicy) && (
            !Number.isInteger(Number(claimPolicy.pointCost)) || Number(claimPolicy.pointCost) < 1
            || !Number.isInteger(Number(claimPolicy.pointIncludedUnits)) || Number(claimPolicy.pointIncludedUnits) < 0
            || !Number.isInteger(Number(claimPolicy.pointExtraUnitCost)) || Number(claimPolicy.pointExtraUnitCost) < 1
        )) {
            show('보험금청구 포인트 정책에는 기본 차감, 포함 장수, 초과 장당 차감가가 필요합니다.', 'error')
            return
        }
        const normalizedRows = rows.map((row) => {
            if (row.featureCode === CLAIM_FAX_PAGE_FEATURE_CODE) {
                return resetPointPricing(row)
            }
            const pointOverage = canConfigurePointOverage(row) && usesPointOverage(row.overagePolicy)
            const claimPricing = pointOverage && row.featureCode === CLAIM_FAX_FEATURE_CODE
            return {
                ...row,
                overagePolicy: pointOverage ? 'ALLOW_WITH_POINT' : 'BLOCK',
                pointCost: pointOverage ? Math.floor(Number(row.pointCost)) : 0,
                pointPricingMode: claimPricing ? POINT_PRICING_BASE_PLUS_EXTRA : POINT_PRICING_PER_UNIT,
                pointIncludedUnits: claimPricing ? Math.floor(Number(row.pointIncludedUnits)) : 0,
                pointExtraUnitCost: claimPricing ? Math.floor(Number(row.pointExtraUnitCost)) : 0,
            }
        })
        setSaving(true)
        try {
            await api.put(`/billing/admin/config/plan-features/${planVersionId}`, normalizedRows)
            show(`${planCode} 기능 정책이 저장되었습니다.`, 'success')
            loadConfigs()
        } catch (err) {
            show(getApiErrorMessage(err, '기능 정책 저장에 실패했습니다.'), 'error')
        } finally {
            setSaving(false)
        }
    }

    const saveStoreProduct = async (mapping) => {
        setSaving(true)
        try {
            await api.put(`/billing/admin/config/channel-products/${mapping.id}`, normalizeMappingPayload(mapping))
            show('스토어 상품 매핑이 저장되었습니다.', 'success')
            loadConfigs()
        } catch (err) {
            show(err?.response?.data?.message || '스토어 상품 매핑 저장에 실패했습니다.', 'error')
        } finally {
            setSaving(false)
        }
    }

    const createStoreProduct = async () => {
        if (!newMapping.productId.trim() || !newMapping.planCode) {
            show('스토어 상품 ID와 플랜을 입력해 주세요.', 'error')
            return
        }
        setSaving(true)
        try {
            await api.post('/billing/admin/config/channel-products', normalizeMappingPayload(newMapping))
            show('스토어 상품 매핑이 추가되었습니다.', 'success')
            setNewMapping({ ...EMPTY_MAPPING, planCode: newMapping.planCode })
            loadConfigs()
        } catch (err) {
            show(err?.response?.data?.message || '스토어 상품 매핑 추가에 실패했습니다.', 'error')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <section>
                <div className="flex items-center justify-center py-12">
                    <div className="text-slate-500">로딩 중...</div>
                </div>
            </section>
        )
    }

    return (
        <section>
            <div className="mb-6 flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold">과금정보관리</h1>
                    <MenuGuideButton slug="subscriptions" title="구독·결제 가이드" anchor="step-02" />
                </div>
            </div>

            <div className="mb-6 rounded-lg border bg-white p-6">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b pb-3">
                    <h2 className="text-lg font-semibold">스토어 상품 매핑 추가</h2>
                    <button
                        className="rounded bg-brand-navy px-4 py-2 text-sm text-white hover:bg-brand-navy/90 disabled:opacity-50"
                        onClick={createStoreProduct}
                        disabled={saving}
                    >
                        {saving ? '저장 중...' : '매핑 추가'}
                    </button>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <Field label="스토어">
                        <StoreSelect value={newMapping.store} onChange={(value) => setNewMapping((prev) => ({ ...prev, store: value }))} />
                    </Field>
                    <Field label="상품 ID">
                        <TextInput value={newMapping.productId} onChange={(value) => setNewMapping((prev) => ({ ...prev, productId: value }))} placeholder="sgez.standard.monthly" />
                    </Field>
                    <Field label="내부 플랜">
                        <PlanSelect plans={plans} value={newMapping.planCode} onChange={(value) => setNewMapping((prev) => ({ ...prev, planCode: value }))} />
                    </Field>
                    <Field label="기간">
                        <NumberInput value={newMapping.periodMonths} onChange={(value) => setNewMapping((prev) => ({ ...prev, periodMonths: value }))} />
                    </Field>
                    <Field label="스토어 표시명">
                        <TextInput value={newMapping.displayName} onChange={(value) => setNewMapping((prev) => ({ ...prev, displayName: value }))} placeholder="Standard Monthly" />
                    </Field>
                    <Field label="스토어 가격">
                        <NumberInput value={newMapping.storePrice} onChange={(value) => setNewMapping((prev) => ({ ...prev, storePrice: value }))} allowEmpty />
                    </Field>
                    <Field label="통화">
                        <TextInput value={newMapping.currency} onChange={(value) => setNewMapping((prev) => ({ ...prev, currency: value }))} />
                    </Field>
                    <Field label="상태">
                        <ActiveSelect value={newMapping.active} onChange={(value) => setNewMapping((prev) => ({ ...prev, active: value }))} />
                    </Field>
                </div>
            </div>

            <div className="space-y-6">
                {plans.map((plan) => (
                    <div key={plan.id} className="rounded-lg border bg-white p-6">
                        <div className="mb-6 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold">{plan.planCode}</h2>
                                <p className="mt-1 text-sm text-slate-500">버전 {plan.versionNo} / planVersionId {plan.planVersionId}</p>
                            </div>
                            <button
                                className="rounded bg-brand-navy px-4 py-2 text-sm text-white hover:bg-brand-navy/90 disabled:opacity-50"
                                onClick={() => savePlan(plan)}
                                disabled={saving}
                            >
                                {saving ? '저장 중...' : '플랜 저장'}
                            </button>
                        </div>

                        <div className="mb-6">
                            <h3 className="mb-4 border-b pb-2 text-base font-medium">플랜 기본정보</h3>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                <Field label="플랜명">
                                    <TextInput value={plan.name || ''} onChange={(value) => updatePlanField(plan.id, 'name', value)} />
                                </Field>
                                <Field label="월 결제금액">
                                    <NumberInput value={plan.monthlyPrice || 0} onChange={(value) => updatePlanField(plan.id, 'monthlyPrice', value)} />
                                </Field>
                                <Field label="기본 체험기간">
                                    <NumberInput value={plan.defaultTrialDays || 0} onChange={(value) => updatePlanField(plan.id, 'defaultTrialDays', value)} />
                                </Field>
                                <Field label="설명">
                                    <TextInput value={plan.description || ''} onChange={(value) => updatePlanField(plan.id, 'description', value)} />
                                </Field>
                                <Field label="활성 여부">
                                    <ActiveSelect value={plan.active} onChange={(value) => updatePlanField(plan.id, 'active', value)} />
                                </Field>
                            </div>
                        </div>

                        <StoreProductTable
                            mappings={storeProductMap[plan.planCode] || []}
                            plans={plans}
                            saving={saving}
                            onChange={updateStoreProductField}
                            onSave={saveStoreProduct}
                        />

                        <div>
                            <div className="mb-4 flex items-center justify-between border-b pb-2">
                                <div>
                                    <h3 className="text-base font-medium">기능별 이용 한도·포인트 차감</h3>
                                    <p className="mt-1 text-xs text-slate-500">월 제공량 소진 후 기능별 포인트 정책이 적용됩니다.</p>
                                </div>
                                <button
                                    className="rounded bg-brand-navy px-4 py-2 text-sm text-white hover:bg-brand-navy/90 disabled:opacity-50"
                                    onClick={() => saveFeatures(plan.planVersionId, plan.planCode)}
                                    disabled={saving}
                                >
                                    {saving ? '저장 중...' : '기능 정책 저장'}
                                </button>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-slate-50 text-slate-600">
                                        <tr>
                                            <th className="px-4 py-3 text-left">기능</th>
                                            <th className="px-4 py-3 text-left">제공</th>
                                            <th className="px-4 py-3 text-left">쿼터 방식</th>
                                            <th className="px-4 py-3 text-left">월 제공량</th>
                                            <th className="px-4 py-3 text-left">단위</th>
                                            <th className="px-4 py-3 text-left">쿼터 소진 후</th>
                                            <th className="px-4 py-3 text-left">포인트 차감 설정</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(featureMap[plan.planCode] || []).map((feature) => {
                                            const pointConfigurable = canConfigurePointOverage(feature)
                                            const pointOverage = pointConfigurable && usesPointOverage(feature.overagePolicy)
                                            const claimPricing = feature.featureCode === CLAIM_FAX_FEATURE_CODE
                                            const claimPagePricing = feature.featureCode === CLAIM_FAX_PAGE_FEATURE_CODE
                                            return (
                                                <tr key={`${plan.planCode}-${feature.featureCode}`} className="border-t">
                                                    <td className="px-4 py-3 font-medium">{feature.featureName}</td>
                                                    <td className="px-4 py-3">
                                                        <ActiveSelect compact value={feature.enabled} onChange={(value) => updateFeatureEnabled(plan.planCode, feature.featureCode, value)} />
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <select
                                                            aria-label={`${plan.planCode} ${feature.featureName} 쿼터 방식`}
                                                            className="min-w-28 rounded border px-2 py-1"
                                                            value={feature.quotaMode || 'NONE'}
                                                            onChange={(e) => updateFeatureQuotaMode(plan.planCode, feature.featureCode, e.target.value)}
                                                        >
                                                            <option value="NONE">미제공</option>
                                                            <option value="LIMITED">횟수 제한</option>
                                                            <option value="UNLIMITED">제한 없음</option>
                                                        </select>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <NumberInput
                                                            compact
                                                            min={0}
                                                            disabled={feature.quotaMode !== 'LIMITED'}
                                                            ariaLabel={`${plan.planCode} ${feature.featureName} 월 제공량`}
                                                            value={feature.monthlyLimit ?? 0}
                                                            onChange={(value) => updateFeatureField(plan.planCode, feature.featureCode, 'monthlyLimit', value)}
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3">{quotaUnitLabel(feature.quotaUnit)}</td>
                                                    <td className="px-4 py-3">
                                                        {claimPagePricing ? (
                                                            <span className="text-xs text-slate-500">청구 건 포인트 정책 적용</span>
                                                        ) : (
                                                            <select
                                                                aria-label={`${plan.planCode} ${feature.featureName} 쿼터 소진 후`}
                                                                className="min-w-40 rounded border px-2 py-1 disabled:bg-slate-100 disabled:text-slate-400"
                                                                disabled={!pointConfigurable}
                                                                value={pointOverage ? 'ALLOW_WITH_POINT' : 'BLOCK'}
                                                                onChange={(e) => updateFeatureOveragePolicy(plan.planCode, feature.featureCode, e.target.value)}
                                                            >
                                                                <option value="BLOCK">추가 사용 불가</option>
                                                                <option value="ALLOW_WITH_POINT">포인트로 추가 사용</option>
                                                            </select>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {claimPagePricing ? (
                                                            <span className="text-xs text-slate-500">별도 차감 없음</span>
                                                        ) : claimPricing ? (
                                                            <div className="grid min-w-[30rem] grid-cols-3 gap-2">
                                                                <label className="text-xs text-slate-600">
                                                                    <span className="mb-1 block">기본 차감</span>
                                                                    <span className="flex items-center gap-1">
                                                                        <NumberInput
                                                                            compact min={0} disabled={!pointOverage}
                                                                            ariaLabel={`${plan.planCode} ${feature.featureName} 기본 차감 포인트`}
                                                                            value={feature.pointCost ?? 0}
                                                                            onChange={(value) => updateFeatureField(plan.planCode, feature.featureCode, 'pointCost', value)}
                                                                        />
                                                                        <span className={pointOverage ? 'text-slate-600' : 'text-slate-300'}>P</span>
                                                                    </span>
                                                                </label>
                                                                <label className="text-xs text-slate-600">
                                                                    <span className="mb-1 block">기본 포함</span>
                                                                    <span className="flex items-center gap-1">
                                                                        <NumberInput
                                                                            compact min={0} disabled={!pointOverage}
                                                                            ariaLabel={`${plan.planCode} ${feature.featureName} 기본 포함 장수`}
                                                                            value={feature.pointIncludedUnits ?? 0}
                                                                            onChange={(value) => updateFeatureField(plan.planCode, feature.featureCode, 'pointIncludedUnits', value)}
                                                                        />
                                                                        <span className={pointOverage ? 'text-slate-600' : 'text-slate-300'}>장</span>
                                                                    </span>
                                                                </label>
                                                                <label className="text-xs text-slate-600">
                                                                    <span className="mb-1 block">초과 장당</span>
                                                                    <span className="flex items-center gap-1">
                                                                        <NumberInput
                                                                            compact min={0} disabled={!pointOverage}
                                                                            ariaLabel={`${plan.planCode} ${feature.featureName} 초과 장당 차감 포인트`}
                                                                            value={feature.pointExtraUnitCost ?? 0}
                                                                            onChange={(value) => updateFeatureField(plan.planCode, feature.featureCode, 'pointExtraUnitCost', value)}
                                                                        />
                                                                        <span className={pointOverage ? 'text-slate-600' : 'text-slate-300'}>P</span>
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1">
                                                                <NumberInput
                                                                    compact
                                                                    min={0}
                                                                    disabled={!pointOverage}
                                                                    ariaLabel={`${plan.planCode} ${feature.featureName} 단위당 차감 포인트`}
                                                                    value={feature.pointCost ?? 0}
                                                                    onChange={(value) => updateFeatureField(plan.planCode, feature.featureCode, 'pointCost', value)}
                                                                />
                                                                <span className={pointOverage ? 'text-slate-600' : 'text-slate-300'}>P</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    )
}

function StoreProductTable({ mappings, plans, saving, onChange, onSave }) {
    return (
        <div className="mb-6">
            <div className="mb-4 border-b pb-2">
                <h3 className="text-base font-medium">스토어 상품 매핑</h3>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                            <th className="px-4 py-3 text-left">스토어</th>
                            <th className="px-4 py-3 text-left">상품 ID</th>
                            <th className="px-4 py-3 text-left">플랜</th>
                            <th className="px-4 py-3 text-left">기간</th>
                            <th className="px-4 py-3 text-left">가격</th>
                            <th className="px-4 py-3 text-left">상태</th>
                            <th className="px-4 py-3 text-left">저장</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mappings.map((mapping) => (
                            <tr key={mapping.id} className="border-t align-top">
                                <td className="px-4 py-3">
                                    <StoreSelect compact value={mapping.store} onChange={(value) => onChange(mapping.id, 'store', value)} />
                                </td>
                                <td className="px-4 py-3">
                                    <TextInput value={mapping.productId || ''} onChange={(value) => onChange(mapping.id, 'productId', value)} />
                                </td>
                                <td className="px-4 py-3">
                                    <PlanSelect plans={plans} value={mapping.planCode || ''} onChange={(value) => onChange(mapping.id, 'planCode', value)} />
                                </td>
                                <td className="px-4 py-3">
                                    <NumberInput compact value={mapping.periodMonths || 1} onChange={(value) => onChange(mapping.id, 'periodMonths', value)} />
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex gap-2">
                                        <TextInput compact value={mapping.currency || 'KRW'} onChange={(value) => onChange(mapping.id, 'currency', value)} />
                                        <NumberInput compact allowEmpty value={mapping.storePrice ?? ''} onChange={(value) => onChange(mapping.id, 'storePrice', value)} />
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <ActiveSelect compact value={mapping.active} onChange={(value) => onChange(mapping.id, 'active', value)} />
                                </td>
                                <td className="px-4 py-3">
                                    <button className="rounded border px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50" disabled={saving} onClick={() => onSave(mapping)}>
                                        저장
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {mappings.length === 0 && (
                            <tr className="border-t">
                                <td className="px-4 py-6 text-center text-slate-500" colSpan={7}>등록된 스토어 상품 매핑이 없습니다.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

function normalizeMappingPayload(mapping) {
    return {
        store: mapping.store,
        channel: mapping.store,
        productId: (mapping.productId || '').trim(),
        externalProductId: (mapping.productId || '').trim(),
        planCode: mapping.planCode,
        periodMonths: Number(mapping.periodMonths) || 1,
        currency: mapping.currency || 'KRW',
        storePrice: mapping.storePrice === '' || mapping.storePrice == null ? null : Number(mapping.storePrice),
        displayName: mapping.displayName || '',
        memo: mapping.memo || '',
        active: Boolean(mapping.active),
    }
}

function StoreSelect({ value, onChange, compact = false }) {
    return (
        <select className={`${compact ? 'w-32' : 'w-full'} rounded border px-3 py-2 text-sm`} value={value || 'apple_iap'} onChange={(e) => onChange(e.target.value)}>
            {Object.entries(STORE_LABELS).map(([store, label]) => (
                <option key={store} value={store}>{label}</option>
            ))}
        </select>
    )
}

function PlanSelect({ plans, value, onChange }) {
    return (
        <select className="w-full rounded border px-3 py-2 text-sm" value={value || ''} onChange={(e) => onChange(e.target.value)}>
            <option value="">선택</option>
            {plans.map((plan) => (
                <option key={plan.planCode} value={plan.planCode}>{plan.name || plan.planCode}</option>
            ))}
        </select>
    )
}

function ActiveSelect({ value, onChange, compact = false }) {
    return (
        <select className={`${compact ? 'w-24' : 'w-full'} rounded border px-3 py-2 text-sm`} value={value ? 'true' : 'false'} onChange={(e) => onChange(e.target.value === 'true')}>
            <option value="true">활성</option>
            <option value="false">비활성</option>
        </select>
    )
}

function TextInput({ value, onChange, placeholder = '', compact = false }) {
    return (
        <input className={`${compact ? 'w-20' : 'w-full'} rounded border px-3 py-2 text-sm`} value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    )
}

function NumberInput({ value, onChange, compact = false, allowEmpty = false, min, disabled = false, ariaLabel }) {
    return (
        <input
            type="number"
            aria-label={ariaLabel}
            min={min}
            disabled={disabled}
            className={`${compact ? 'w-24' : 'w-full'} rounded border px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400`}
            value={value ?? ''}
            onChange={(e) => onChange(allowEmpty && e.target.value === '' ? '' : Number(e.target.value) || 0)}
        />
    )
}

function Field({ label, children }) {
    return (
        <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
            {children}
        </div>
    )
}
